import { usePreview } from '../../shares/views/PreviewView/PreviewContext'
import { useVersioning } from '../../versioning'
import { useGetPresentationAssetsByArtboardQuery } from '@sketch/gql-types'

interface UsePresentationFileProps {
  artboardUuid: string
}

/**
 * When we arrive on a prototype we just have the start Artboard UUID. However
 * presentation file assets for the whole doc are currently accessed via the
 * Page type. So we have to go via the Artboard's page to get the presentation
 * file info.
 */
export const usePresentationFile = ({
  artboardUuid,
}: UsePresentationFileProps) => {
  const context = useVersioning()
  const previewContext = usePreview()

  const documentId = context.loading
    ? null
    : (context?.currentVersion.document?.identifier ?? null)

  const prFileQuery = useGetPresentationAssetsByArtboardQuery({
    variables: {
      artboardUuid: artboardUuid,
      documentId: documentId ?? '',
    },
    skip: !context || context.loading,
  })

  // in previews, we have to massage this a bit
  if (previewContext) {
    const prototypes = previewContext.prototypes || []

    const matchedPrototype = prototypes.find(
      prototype => prototype.uuid === artboardUuid
    )
    const page = matchedPrototype?.page ?? null

    return {
      page,
      documentVersionShortId: null,
      isLoading: false,
      hasError: false,
      hasNotFoundError: false,
      isRendering: false,
    }
  }

  const isRendering = context?.renderStatus !== 'ready'
  const page = prFileQuery.data?.artboardInDocumentWithUuid?.page
  const isLoading = prFileQuery.loading || context.loading
  const hasError = prFileQuery.error
  const hasNotFoundError = prFileQuery.error?.graphQLErrors.some(
    x => x.extensions?.code === 'NOT_FOUND'
  )

  return {
    page,
    /**
     * The version the returned page — and so its manifest and its image token —
     * actually belongs to. Anything derived from this page has to be scoped to
     * this version rather than to whichever version the route is on, because
     * this query can still be serving the previous version's page while it
     * refetches, and a token minted for one page row is rejected for another.
     */
    documentVersionShortId:
      prFileQuery.data?.artboardInDocumentWithUuid?.documentVersionShortId ??
      null,
    isLoading,
    hasError,
    hasNotFoundError,
    isRendering,
  }
}
